'use client';

import LinkedInShortLogo from '@/components/Logos/LinkedInShortLogo';
import WikipediaWLogo from '@/components/Logos/WikipediaWLogo';
import { FATHOM_EVENTS } from '@/lib/fathom/fathom-events';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import type { AboutSocial } from '@/platform/pages/about-model';

interface SocialLinkProps {
  social: AboutSocial;
  name: string;
}

const NETWORK_LABEL: Record<AboutSocial['type'], string> = {
  linkedin: 'LinkedIn',
  wikipedia: 'Wikipedia',
  web: 'website',
};

// Generic globe used for the `web` social type (no brand logo applies).
function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.4 4 5.6 4 9s-1.5 6.6-4 9c-2.5-2.4-4-5.6-4-9s1.5-6.6 4-9Z" />
    </svg>
  );
}

// Icon per social.type — explicit map so a valid `web` type never falls through to the
// LinkedIn logo. Never derived from copy.
function SocialIcon({ type, className }: { type: AboutSocial['type']; className?: string }) {
  switch (type) {
    case 'wikipedia':
      return <WikipediaWLogo monochrome className={className} />;
    case 'web':
      return <GlobeIcon className={className} />;
    case 'linkedin':
      return <LinkedInShortLogo monochrome className={className} />;
  }
}

// External profile link on a person card. Client-only so the click can fire the
// co-located Fathom event.
export function SocialLink({ social, name }: SocialLinkProps) {
  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name} on ${NETWORK_LABEL[social.type]}`}
      onClick={() => trackFathomEvent(FATHOM_EVENTS.about.team.social.click)}
      className="shrink-0 text-gray-1 transition-colors hover:text-gray-2"
    >
      {/* 36px, per the design's 36×36 glyph frame (Figma 17022:3457) — was 24px. */}
      <SocialIcon type={social.type} className="size-9 rounded-xs" />
    </a>
  );
}
